// Fetch wrapper: same-origin cookies, CSRF header, group switcher header, problem+json errors.

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

const GROUP_KEY = 'an.group';
export const getGroup = () => {
  try {
    return localStorage.getItem(GROUP_KEY);
  } catch {
    return null;
  }
};
export const setGroup = (id: string | null) => {
  try {
    if (id) localStorage.setItem(GROUP_KEY, id);
    else localStorage.removeItem(GROUP_KEY);
  } catch {
    /* private mode */
  }
};

export async function api<T = any>(path: string, opts: { method?: string; body?: unknown; form?: FormData; signal?: AbortSignal; timeoutMs?: number } = {}): Promise<T> {
  const headers: Record<string, string> = { 'x-an-csrf': '1' };
  const g = getGroup();
  if (g) headers['x-an-group'] = g;
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 30_000);
  opts.signal?.addEventListener('abort', () => ctrl.abort());
  let res: Response;
  try {
    res = await fetch(path, { method: opts.method ?? (opts.body !== undefined || opts.form ? 'POST' : 'GET'), credentials: 'include', headers, body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined), signal: ctrl.signal });
  } catch (e) {
    throw new ApiError(0, 'network', (e as Error).name === 'AbortError' ? 'Request timed out' : 'Network error — check your connection');
  } finally {
    clearTimeout(timer);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  const body = ct.includes('json') ? await res.json() : await res.blob();
  if (!res.ok) {
    const b = body as { title?: string; code?: string; details?: unknown; errors?: unknown };
    if (res.status === 401 && !path.startsWith('/v1/auth') && !location.pathname.startsWith('/login')) {
      const next = encodeURIComponent(location.pathname + location.search);
      location.href = `/login?next=${next}`;
    }
    throw new ApiError(res.status, b.code ?? 'error', b.title ?? `Request failed (${res.status})`, b.details ?? b.errors);
  }
  return body as T;
}

export const get = <T = any>(p: string, signal?: AbortSignal) => api<T>(p, { signal });
export const post = <T = any>(p: string, body?: unknown) => api<T>(p, { method: 'POST', body: body ?? {} });
export const patch = <T = any>(p: string, body: unknown) => api<T>(p, { method: 'PATCH', body });
export const del = <T = any>(p: string) => api<T>(p, { method: 'DELETE' });
export const upload = <T = any>(p: string, form: FormData) => api<T>(p, { method: 'POST', form, timeoutMs: 120_000 });

export function qs(params: Record<string, unknown>) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'object' && !Array.isArray(v)) for (const [fk, fv] of Object.entries(v as Record<string, unknown>)) { if (fv !== undefined && fv !== '' && fv !== null) u.set(`${k}[${fk}]`, String(fv)); }
    else u.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

export async function downloadBlob(path: string, filename: string) {
  const b = await api<Blob>(path);
  const url = URL.createObjectURL(b);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
